import { useCallback, useRef, useState } from "react";

import type { EditorImage } from "../editor/types";
import { releaseEditorImage } from "../utils/files";

/** The editable part of an image. Updates are immutable, so references suffice. */
type Snapshot = Pick<
  EditorImage,
  "coverage" | "coverageEffect" | "regions" | "restorations" | "dirty" | "saved"
>;

interface History {
  past: Snapshot[];
  future: Snapshot[];
}

const HISTORY_LIMIT = 80;

function snapshot(image: EditorImage): Snapshot {
  const { coverage, coverageEffect, regions, restorations, dirty, saved } = image;
  return { coverage, coverageEffect, regions, restorations, dirty, saved };
}

export type ImageUpdater = (image: EditorImage) => EditorImage;

/**
 * Owns the loaded images, the active one, and a per-image undo history.
 * Continuous gestures call `checkpoint()` once and then update with
 * `{ history: false }`, so a whole drag undoes in one step.
 */
export function useImageStore() {
  const [images, setImagesState] = useState<EditorImage[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [, setRevision] = useState(0);
  const imagesRef = useRef<EditorImage[]>([]);
  const activeIndexRef = useRef(0);
  const historyRef = useRef(new Map<string, History>());

  const setImages = useCallback((next: EditorImage[]) => {
    imagesRef.current = next;
    setImagesState(next);
  }, []);

  const select = useCallback((index: number) => {
    activeIndexRef.current = index;
    setActiveIndex(index);
  }, []);

  const historyOf = (id: string) => {
    let history = historyRef.current.get(id);
    if (!history) {
      history = { past: [], future: [] };
      historyRef.current.set(id, history);
    }
    return history;
  };

  const record = useCallback((image: EditorImage) => {
    const history = historyOf(image.id);
    history.past.push(snapshot(image));
    if (history.past.length > HISTORY_LIMIT) history.past.shift();
    history.future = [];
    setRevision((value) => value + 1);
  }, []);

  const checkpoint = useCallback(() => {
    const image = imagesRef.current[activeIndexRef.current];
    if (image) record(image);
  }, [record]);

  const updateImage = useCallback(
    (id: string, updater: ImageUpdater, options: { history?: boolean } = {}) => {
      const current = imagesRef.current.find((image) => image.id === id);
      if (!current) return;
      const next = updater(current);
      if (next === current) return;
      if (options.history !== false) record(current);
      setImages(imagesRef.current.map((image) => (image.id === id ? next : image)));
    },
    [record, setImages],
  );

  const updateActive = useCallback(
    (updater: ImageUpdater, options?: { history?: boolean }) => {
      const image = imagesRef.current[activeIndexRef.current];
      if (image) updateImage(image.id, updater, options);
    },
    [updateImage],
  );

  const travel = useCallback(
    (from: "past" | "future") => {
      const image = imagesRef.current[activeIndexRef.current];
      if (!image) return false;
      const history = historyOf(image.id);
      const target = history[from].pop();
      if (!target) return false;
      history[from === "past" ? "future" : "past"].push(snapshot(image));
      setImages(
        imagesRef.current.map((item) => (item.id === image.id ? { ...item, ...target } : item)),
      );
      setRevision((value) => value + 1);
      return true;
    },
    [setImages],
  );

  const add = useCallback(
    (loaded: EditorImage[], replace: boolean) => {
      if (replace) {
        imagesRef.current.forEach(releaseEditorImage);
        historyRef.current.clear();
      }
      const firstNew = replace ? 0 : imagesRef.current.length;
      setImages(replace ? loaded : [...imagesRef.current, ...loaded]);
      select(firstNew);
    },
    [select, setImages],
  );

  const remove = useCallback(
    (index: number) => {
      const target = imagesRef.current[index];
      if (!target) return;
      historyRef.current.delete(target.id);
      releaseEditorImage(target);
      const next = imagesRef.current.filter((_, at) => at !== index);
      const active = activeIndexRef.current;
      setImages(next);
      select(Math.max(0, Math.min(next.length - 1, active > index ? active - 1 : active)));
    },
    [select, setImages],
  );

  const reorder = useCallback(
    (from: number, to: number) => {
      const current = imagesRef.current;
      if (from === to || from < 0 || to < 0 || from >= current.length || to >= current.length) {
        return;
      }
      const next = [...current];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      setImages(next);
      // Keep looking at the same picture rather than the same slot.
      const activeId = current[activeIndexRef.current]?.id;
      select(Math.max(0, next.findIndex((image) => image.id === activeId)));
    },
    [select, setImages],
  );

  const clear = useCallback(() => {
    imagesRef.current.forEach(releaseEditorImage);
    historyRef.current.clear();
    setImages([]);
    select(0);
  }, [select, setImages]);

  /** Applies `updater` to every image as one undo step per image. */
  const updateAll = useCallback(
    (updater: ImageUpdater) => {
      imagesRef.current.forEach(record);
      setImages(imagesRef.current.map(updater));
    },
    [record, setImages],
  );

  const resetHistory = useCallback(() => {
    historyRef.current.clear();
    setRevision((value) => value + 1);
  }, []);

  const activeImage = images[activeIndex] as EditorImage | undefined;
  const activeHistory = activeImage ? historyRef.current.get(activeImage.id) : undefined;

  return {
    images,
    imagesRef,
    activeIndex,
    activeImage,
    canUndo: Boolean(activeHistory?.past.length),
    canRedo: Boolean(activeHistory?.future.length),
    select,
    checkpoint,
    updateImage,
    updateActive,
    updateAll,
    undo: useCallback(() => travel("past"), [travel]),
    redo: useCallback(() => travel("future"), [travel]),
    add,
    remove,
    reorder,
    clear,
    resetHistory,
  };
}

export type ImageStore = ReturnType<typeof useImageStore>;
